import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import * as io from 'socket.io-client';

import { ToastrService, IndividualToastrConfig, ActiveToast } from 'ngx-toastr';
import { AppinfoService } from './appinfo.service';
import { UserService } from './user.service';

import { AppSettings } from './../app.settings';

export type SocketMessageDataType = 'print' | 'percent' | 'json' | 'printJson';
export interface PrintJsonMessage {
	typ?: 'err';
	time: string;
	cate: string;
	print?: string;
	// [msg: string]: any;
}
// Socket message passed from backend
export interface SocketMessage {
	processId: string;
	processType: 'Upload';
	isFinal: boolean;
	isRealTime: boolean; // TODO
	message: {
		type: SocketMessageDataType;
		content: any;
	};
	timeStamp: Date;
	// These will only exist in final message
	isError?: boolean;
	beginTime?: Date;
	endTime?: Date | string;
	cancelled?: boolean;
}
// Socket message interface for frontend use
interface SocketMessageFE_ {
	processId: string;
	processType: ProcessType;
	isFinal: boolean;
	isError: boolean;
	isCanceled: boolean;
	dataType: SocketMessageDataType;
	// data: any;
	timeStamp: Date | string;
	info?: {[prop: string]: any};
}
interface SocketMessageFE_Print extends SocketMessageFE_ {
	dataType: 'print';
	data: string;
}
interface SocketMessageFE_PrintJson extends SocketMessageFE_ {
	dataType: 'printJson';
	data: {
		[msgDataCate: string]: PrintJsonMessage[];
	};
}
interface SocketMessageFE_Other extends SocketMessageFE_ {
	dataType: 'percent' | 'json';
	data: any;
}
export type SocketMessageFE = SocketMessageFE_Print | SocketMessageFE_PrintJson | SocketMessageFE_Other;

export type ProcessType = 'Upload' | 'QC' | 'TOPO' | 'QR' | 'AnalyzeFishbone';

export interface InitProcess {
	processType: ProcessType;
	tempId: string;
	processId?: string;
	status?: string;
	info?: {[prop: string]: any};
}

// Socket logs by processId. This is for use of displaying logs in common-socket-logs
export interface SocketLogs {
	[processId: string]: SocketLog;
}
export interface SocketLog {
	socketMsg: SocketMessageFE[];
	toastId?: number;
	isCurrent?: boolean; // Is current message to display in the logs page
	isFinished?: boolean;
	isError?: boolean;
}

@Injectable()
export class SocketService {

	private socket: SocketIOClient.Socket;
	public toastrConfig: Partial<IndividualToastrConfig> = {
		// toastComponent: CommonToastComponent,
		// timeOut: null,
		// extendedTimeOut: 1000000,
		enableHtml: true
	};

	// Store whether a process's logs has been refreshed or not after socket re-connecting
	// This is for case: socket disconnects then connects, all the logs will be sent one more time. The old logs need to be cleaned or it will repeat.
	public refreshedProcessesById: { [processId: string]: boolean };

	// On initing / preparing a process
	public onInitProcess: Subject<InitProcess> = new Subject();
	//
	public filePostSubject: Subject<SocketMessageFE> = new Subject();
	//
	public qcSocketSubject: Subject<SocketMessageFE> = new Subject();
	public qrSocketSubject: Subject<SocketMessageFE> = new Subject();
	//
	public buildTopoSubject: Subject<SocketMessageFE> = new Subject();
	//
	fishboneSubject: Subject<SocketMessageFE> = new Subject();
	constructor(
		private toastr: ToastrService,
		private appinfoService: AppinfoService,
		private userService: UserService
	) { }

	getSocketId() {
		return this.socket.id;
	}
	connectSocket() {
		console.log('connect socket...');
		if (this.socket && this.socket.disconnected) {
			this.socket.connect();
		} else if (!this.socket) {
			try {
				const socket = io(AppSettings.serviceRootUrl, AppSettings.socketConnectOption);
				this.socket = socket;
				console.log('io connect: ', socket.id);
				console.log('io connect socket: ', socket.toString());
			} catch (err) {
				console.log(err);
			}
			this.socket.on('message', (data) => {
				console.log(data);
				const formattedData = this.handleData(data);
				switch (formattedData.processType) {
					case 'Upload':
						this.filePostSubject.next(formattedData);
						break;
					case 'QC':
						this.qcSocketSubject.next(formattedData);
						break;
					case 'TOPO':
						this.buildTopoSubject.next(formattedData);
						break;
					case 'QR':
						this.qrSocketSubject.next(formattedData);
						break;
					case 'AnalyzeFishbone':
						this.fishboneSubject.next(formattedData);
						break;
				}
			});
			this.socket.on('disconnect', (data) => {
				console.log('dddddddddddddddddddisconnect', data);
			});
			this.socket.on('error', (err) => {
				console.log(err);
			});
			this.socket.on('connect', () => {
				console.log('connect event: ', this.socket.id);
				this.socket.emit('init', {
					userSubscriptionId: this.userService.getUserInfo().userSubId,
					sessionId: this.appinfoService.getSessionUuid()
				});
			});
		}
	}
	disconnectSocket() {
		console.log('manually disconnect socket')
		this.socket.disconnect();
	}
	sendMessage(msg: string) {
		this.socket.send(msg);
	}
	// Generate toast message
	public generateToastMessage(type: 'processing' | 'final', msg?: string, titleMsg?: string): string {
		msg = msg || '';
		switch (type) {
			case 'processing':
				titleMsg = titleMsg || 'Uploading';
				return `<div>${titleMsg} <div class="loading-dots"></div></div>
				<div>${msg}</div>`;
			case 'final':
				// Set Default msg
				msg = msg || `<div>Successfully uploaded</div>`;
				return msg;
		}
	}
	// Generate toast
	public generateToast(type: 'info' | 'success' |'error' | 'warning', message: string, onTapCallback? ): ActiveToast<any> {
		let toast: ActiveToast<any>;
		const config: Partial<IndividualToastrConfig> = Object.assign({}, this.toastrConfig);
		config.timeOut = 10000;
		config.extendedTimeOut = 10000;
		switch (type) {
			case 'info':
				toast = this.toastr.info(message, null, config);
				break;
			case 'success':
				config.tapToDismiss = true;
				toast = this.toastr.success(message, null, config);
				break;
			case 'error':
				toast = this.toastr.error(message, null, this.toastrConfig);
				break;
			case 'warning':
				toast = this.toastr.warning(message, null, this.toastrConfig);
		}
		if (onTapCallback) {
			toast.onTap.subscribe(() => {
				onTapCallback();
			});
		}
		return toast;
	}
	// Process with socket info
	registerProcessById(processId: string, processesById): SocketLog {
		// Set already existed process isCurrent as false
		for (const id of Object.keys(processesById)) {
			processesById[id].isCurrent = false;
		}
		// Register the new process
		const process = {
			toastId: null,
			socketMsg: [],
			isCurrent: true // Make the latest received process the current one to display in the logs page
		};
		processesById[processId] = process;
		return process;
	}
	// Get current socket log from SocketLogs
	getCurrentSocketLog(processId: string, socketLogs: SocketLogs): SocketLog {
		if (!processId) {
			for (const id of Object.keys(socketLogs)) {
				if (socketLogs[id].isCurrent) {
					processId = id;
					break;
				}
			}
		}
		if (socketLogs[processId]) {
			return socketLogs[processId];
		}
		return null;
	}
	// Remove toast
	removeToast(toastId) {
		if (toastId || toastId === 0) {
			this.toastr.remove(toastId);
		}
	}

	public onSocketUpdate(data: SocketMessageFE, logsBehaviorSubject: BehaviorSubject<{[id: string]: SocketLog}>, messages: {success: string, canceled: string}, onFinal?) {
		// All socket logs
		const processesById = logsBehaviorSubject.getValue();
		// Socket logs of the current process
		const process = processesById[data.processId] || this.registerProcessById(data.processId, processesById);
		// Clear old socket logs if socket reconnects
		if (this.refreshedProcessesById && !this.refreshedProcessesById[data.processId]) {
			process.socketMsg.length = 0;
			// Do not use process.socketMsg = [] to clear messages, since the object reference will change and causing no-display issue
			// process.socketMsg = [];
			this.refreshedProcessesById[data.processId] = true;
		}
		// Push socket message to process.socketMsg
		process.socketMsg.push(data);
		// Current toastId
		const toastId = process.toastId;
		this.removeToast(toastId);
		// Finished
		if (data.isFinal) {
			process.isFinished = true;
			if (data.isError) { // Error
				process.isError = true;
				// Toast error
				const err = data.data;
				// this.removeToast(toastId);
				if (err && err.errorMessage) {
					process.toastId = this.generateToast('error', this.generateToastMessage('final', err.errorMessage + '' + err.errorDetail)).toastId;
					// this.generateToast('success', this.generateToastMessage('final', message.content));
				}
			} else if (data.isCanceled) { // Canceled
				process.toastId = this.generateToast('error', this.generateToastMessage('final', messages.canceled)).toastId;
			} else { // Completed
				// this.removeToast(toastId);
					process.toastId = this.generateToast('success', this.generateToastMessage('final', messages.success)).toastId;
					// this.generateToast('success', this.generateToastMessage('final', message.content));
					// process.socketMsg
			}
			if (onFinal) {
				onFinal();
			}
		}
		// // Not Fnished
		// else if (data.dataType === 'print') {
		// 	// if (!process.completed) {
		// 	// 	this.removeToast(toastId);
		// 	// 	const toast = this.generateToast('info', this.generateToastMessage('processing', data.data, 'Parsing'));
		// 	// 	process.toastId = toast.toastId;
		// 	// }
		// }
		// else if (data.dataType === 'printJson') {
		// 	const printJsonMsgs: PrintJsonMessage[] = [];
		// 	(<PrintJsonMessage[]>data.data).forEach(d => {
		// 		const msg: PrintJsonMessage = {
		// 			time: d.time
		// 		};
		// 		if (d.typ) {
		// 			msg.typ = d.typ;
		// 		}
		// 		let str = '';
		// 		Object.keys(d).forEach(key => {
		// 			if (key !== 'time' && key !== 'typ') {
		// 				str = str + key + ': ' + d[key] + '\n';
		// 			}
		// 		});
		// 		msg.print = str;
		// 		printJsonMsgs.push(msg);
		// 	});
		// 	// Sort messages by time
		// 	data.data = printJsonMsgs.sort((a, b) => {
		// 		const timeA = a.time ? new Date(a.time) : new Date();
		// 		const timeB = b.time ? new Date(b.time) : new Date();
		// 		if (timeA.getTime() > timeB.getTime()) {
		// 			return 1;
		// 		} else if (timeA.getTime() < timeB.getTime()) {
		// 			return -1;
		// 		} else {
		// 			return 0;
		// 		}
		// 	});
		// } else if (data.dataType === 'percent') {
		// 	// TODO
		// 	// process.percent = Math.floor(data.data.batchNumber / data.data.totalBatch * 100);
		// }
		logsBehaviorSubject.next(processesById);
	}

	// // Handle socket message
	// toastMessage() {}

	private handleData(data: string): SocketMessageFE {
		const dataObj: SocketMessage = JSON.parse(data);
		console.log(dataObj);
		const changeDate = (dateStr) => {
			const date = dateStr ? new Date(dateStr + ' z') : null;
			return date ? date.toLocaleDateString() + ' ' + date.toLocaleTimeString() : null
		}
		const formattedData: SocketMessageFE = {
			processId: dataObj.processId,
			processType: dataObj.processType,
			isFinal: dataObj.isFinal,
			isError: dataObj.isError,
			isCanceled: dataObj.cancelled,
			dataType: (<any>dataObj.message.type),
			data: dataObj.message.content,
			timeStamp: changeDate(dataObj.timeStamp) 
		};
		if (formattedData.dataType === 'printJson') {
			const printJsonMsgsByCate: {
				[cate: string]: PrintJsonMessage[]
			} = {};
			if (dataObj.message.content) {
				dataObj.message.content.forEach(d => {
					const msg: PrintJsonMessage = {
						time: changeDate(d.time),
						cate: d.cate
					};
					if (d.type) {
						msg.typ = d.type;
					}
					msg.print = d.desc + (d.val === undefined ? '' : ': ' + d.val) + '\n';
					if (!printJsonMsgsByCate[msg.cate]) {
						printJsonMsgsByCate[msg.cate] = [];
					}
					printJsonMsgsByCate[msg.cate].push(msg);
				});
			}
			Object.keys(printJsonMsgsByCate).forEach(cate => {
				printJsonMsgsByCate[cate].sort((a, b) => {
					const timeA = a.time ? new Date(a.time) : new Date();
					const timeB = b.time ? new Date(b.time) : new Date();
					if (timeA.getTime() > timeB.getTime()) {
						return 1;
					} else if (timeA.getTime() < timeB.getTime()) {
						return -1;
					} else {
						return 0;
					}
				});
			})
			// Sort messages by time
			formattedData.data = printJsonMsgsByCate;
		}
		// switch (dataObj.message.type) {
		// 	case 'json':
		// 		if (dataObj.content && dataObj.content.errorMessage) {
		// 			formattedData.dataType = 'error';
		// 			this.toastr.error(dataObj.content.errorMessage);
		// 			this.toastr.error(dataObj.content.errorDetail);
		// 			console.log(dataObj);
		// 		} else {
		// 			formattedData.dataType = 'final';
		// 		}
		// 		break;
		// 	case 'print':
		// 		formattedData.data = 'string';
		// 		break;
		// 	case 'percent':
		// 		formattedData.dataType = 'info';
		// 		break;
		// 	default:
		// 		console.log('data type can not be handled.');
		// }
		return formattedData;
	}

}
